import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import {
  ArrowRight,
  Bell,
  ChevronRight,
  Heart,
  Home as HomeIcon,
  Info,
  Loader2,
  Search as SearchIcon,
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import TrustScoreRing from '../components/TrustScoreRing';
import { useProfile } from '../contexts/ProfileContext';

const PLACE_TYPE_LABELS = {
  private_room_shared_house: 'Room or basement',
  basement_apartment: 'Basement apartment',
  studio: 'Studio',
  one_bedroom: '1 bedroom',
  open_to_anything: 'Any place type',
};

/**
 * "My Sturent" dashboard — redesigned per the Airbnb-style pattern.
 *
 * The page feels like a tool, not a landing page. Structure:
 *   1. Greeting (calm, not H1)
 *   2. Search surface (primary action — input + filter pills + Search button)
 *   3. Quick filter row (horizontal scroll on mobile)
 *   4. Recommended ("Homes near [School]") — empty state now
 *   5. Saved homes (compact empty state when 0)
 *   6. Two-column engagement row: profile completion + trust score
 *   7. Alerts compact
 *   8. Helpful tips (kept from earlier version)
 *   9. Footer (compact)
 */
const Home = () => {
  const navigate = useNavigate();
  const { bundle, loading } = useProfile();
  const [showTrust, setShowTrust] = useState(false);
  // Keep the search box empty by default. The student's saved school is
  // surfaced as a placeholder and drives proximity sort via near_lat/near_lng
  // — we deliberately do NOT put the school name into the `q` query parameter
  // (it's a regex match against title/description/city/neighborhood and rarely
  // matches a school's full name).
  const [searchInput, setSearchInput] = useState('');

  if (loading || !bundle) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  const u = bundle.user;
  const completion = bundle.profile_completion;
  const score = bundle.trust_score;
  const savedCount = bundle.counts?.saved_listings || 0;
  const alertsCount = bundle.counts?.saved_searches || 0;
  const isLandlord = u.role === 'landlord' || u.role === 'admin';

  const budgetLabel = u.monthly_budget_cad ? `Up to $${u.monthly_budget_cad}` : 'Set a budget';
  const placeLabel =
    u.place_types_interested?.length
      ? (PLACE_TYPE_LABELS[u.place_types_interested[0]] || 'Any place type')
      : 'Any place type';
  const moveInLabel =
    u.expected_move_in_date
      ? `By ${new Date(u.expected_move_in_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`
      : u.move_in_urgency === 'within_30_days'
        ? 'Within 30 days'
        : u.move_in_urgency === '1_to_3_months'
          ? '1–3 months'
          : 'Anytime';

  const incomplete = completion.items.filter((i) => !i.complete);

  const schoolLabel = u.school_name || 'your school';

  const doSearch = (extraQuery = '') => {
    const params = new URLSearchParams();
    if (searchInput) params.set('q', searchInput);
    if (u.monthly_budget_cad) params.set('max_price', String(u.monthly_budget_cad));
    // NOTE: deliberately NOT forwarding `place_types_interested` — the student-side
    // enum (private_room_shared_house, etc.) doesn't match the listing-side enum
    // (private_room, basement_apartment, ...). Users can refine from the filter
    // pills on the search page instead of over-filtering by default.
    if (extraQuery) {
      const [k, v] = extraQuery.split('=');
      params.set(k, v);
    }
    navigate(`/search?${params.toString()}`);
  };

  const quickFilters = [
    { label: 'Under $900', param: 'max_price=900' },
    { label: 'Private room', param: 'place_types=private_room' },
    { label: 'Basement apartment', param: 'place_types=basement_apartment' },
    { label: '1 bedroom', param: 'place_types=one_bedroom' },
    {
      label: 'Available within 30 days',
      // Backend listing search filter key is `move_in_by=YYYY-MM-DD`.
      param: `move_in_by=${new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10)}`,
    },
  ];

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="home-page">
      <AppHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-5">
        {/* 1. Greeting */}
        <section data-testid="greeting-block">
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900">
            Hi, {u.first_name}.
          </h1>
          <p className="mt-1 text-sm sm:text-base text-slate-600">
            {isLandlord ? (
              <>Ready to get your place in front of verified students?</>
            ) : u.school_name ? (
              <>
                Looking for a place near{' '}
                <span className="font-semibold text-slate-900">{u.school_name}</span>.
              </>
            ) : (
              <>
                Tell us your school to see better matches.{' '}
                <Link
                  to="/profile/edit"
                  className="text-amber-600 font-medium hover:underline"
                  data-testid="add-school-cta"
                >
                  Add school
                </Link>
              </>
            )}
          </p>
        </section>

        {/* Landlord CTA — the whole point of the logged-in landlord experience in Phase 1 */}
        {isLandlord && (
          <section data-testid="landlord-cta-block">
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200/60">
              <CardContent className="p-5 sm:p-6">
                <h2 className="font-heading font-bold text-lg sm:text-xl text-slate-900">
                  List a new place.
                </h2>
                <p className="mt-1 text-sm text-slate-700">
                  Takes about 10 minutes. We review within 24 hours — nothing goes live until it's verified.
                </p>
                <Button
                  className="mt-4 btn-primary h-11 px-5"
                  onClick={() => navigate('/listings/new')}
                  data-testid="landlord-create-listing-cta"
                >
                  <HomeIcon className="h-4 w-4 mr-2" /> Create a listing
                </Button>
              </CardContent>
            </Card>
          </section>
        )}

        {/* 2. Search surface */}
        <section data-testid="search-surface" className="space-y-3">
          <div className="flex flex-col md:flex-row md:items-center gap-2 p-3 bg-white border border-slate-200 rounded-2xl shadow-soft">
            <div className="flex-1">
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={
                  u.school_name
                    ? `Search homes near ${u.school_name}`
                    : 'Search by school, city, or neighborhood'
                }
                className="h-12 border-0 bg-slate-50/50 focus-visible:ring-2 focus-visible:ring-amber-200 rounded-xl text-sm sm:text-base"
                data-testid="home-search-input"
              />
            </div>
            <Button
              className="h-12 px-6 btn-primary"
              onClick={() => doSearch()}
              data-testid="home-search-btn"
            >
              <SearchIcon className="h-4 w-4 mr-2" />
              Search homes
            </Button>
          </div>

          {/* Filter pills (current prefs) */}
          <div className="flex flex-wrap gap-2" data-testid="active-filter-pills">
            <button
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-amber-400 text-slate-700"
              onClick={() => navigate('/profile/edit')}
              data-testid="pill-budget"
            >
              {budgetLabel}
              <ChevronRight className="h-3 w-3 text-slate-400" />
            </button>
            <button
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-amber-400 text-slate-700"
              onClick={() => navigate('/profile/edit')}
              data-testid="pill-place"
            >
              {placeLabel}
              <ChevronRight className="h-3 w-3 text-slate-400" />
            </button>
            <button
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-amber-400 text-slate-700"
              onClick={() => navigate('/profile/edit')}
              data-testid="pill-movein"
            >
              {moveInLabel}
              <ChevronRight className="h-3 w-3 text-slate-400" />
            </button>
            <button
              className="text-xs font-medium text-slate-500 hover:text-amber-600 ml-auto"
              onClick={() => navigate('/profile/edit')}
              data-testid="edit-all-filters"
            >
              Edit all filters
            </button>
          </div>
        </section>

        {/* 3. Quick filters */}
        <section data-testid="quick-filters" className="relative">
          <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin">
            {quickFilters.map((q) => (
              <button
                key={q.label}
                className="flex-shrink-0 snap-start whitespace-nowrap text-xs sm:text-sm font-medium px-4 py-2 rounded-full border border-slate-200 bg-white hover:border-amber-400 hover:text-amber-700 text-slate-700 transition-colors"
                onClick={() => doSearch(q.param)}
                data-testid={`quick-${q.param}`}
              >
                {q.label}
              </button>
            ))}
          </div>
        </section>

        {/* 4. Recommended */}
        <section data-testid="recommended-block">
          <h2 className="font-heading font-semibold text-base sm:text-lg text-slate-900 mb-2">
            Homes near {schoolLabel}
          </h2>
          <Card className="bg-white border-slate-200/70 shadow-soft">
            <CardContent className="p-5 sm:p-6 text-center">
              <div className="mx-auto w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center mb-3">
                <HomeIcon className="h-6 w-6 text-amber-600" />
              </div>
              <p className="text-sm text-slate-700 leading-relaxed max-w-md mx-auto">
                We're verifying new listings every week. Be the first to see them when they go live
                near {schoolLabel}.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 border-slate-300"
                onClick={() => navigate('/search')}
                data-testid="recommended-alert-btn"
              >
                <Bell className="h-4 w-4 mr-2" />
                Notify me when they do
              </Button>
            </CardContent>
          </Card>
        </section>

        {/* 5. Saved homes — compact */}
        <section data-testid="saved-block">
          <h2 className="font-heading font-semibold text-base sm:text-lg text-slate-900 mb-2">
            Saved homes
          </h2>
          {savedCount === 0 ? (
            <p className="text-sm text-slate-500">
              Tap the heart on any listing to keep track of it.
            </p>
          ) : (
            <p className="text-sm text-slate-600">
              {savedCount} saved.{' '}
              <Link to="/saved" className="text-amber-600 font-medium hover:underline">
                See all
              </Link>
            </p>
          )}
        </section>

        {/* 6. Engagement — profile completion + trust score (two-column on tablet+) */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="engagement-row">
          {completion.percent < 100 && (
            <Card
              className="bg-gradient-to-br from-amber-50 to-orange-50/40 border-amber-200/60"
              data-testid="profile-completion-block"
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-heading font-bold text-sm sm:text-base text-slate-900">
                      Complete your profile
                    </h3>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Landlords reply faster to students with full profiles.
                    </p>
                  </div>
                  <span
                    className="text-lg font-heading font-bold text-amber-700"
                    data-testid="completion-percent"
                  >
                    {completion.percent}%
                  </span>
                </div>
                <Progress value={completion.percent} className="h-2 mb-3" />
                <ul className="space-y-1 mb-3">
                  {incomplete.slice(0, 3).map((item) => (
                    <li key={item.key} className="flex items-center justify-between text-xs text-slate-700">
                      <span>{item.label}</span>
                      <span className="font-semibold text-amber-700">+{item.weight}%</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="sm"
                  className="btn-primary w-full"
                  onClick={() => navigate('/profile/edit')}
                  data-testid="completion-cta"
                >
                  Complete profile
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="bg-white border-slate-200/70 shadow-soft" data-testid="trust-score-block">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-4">
                <TrustScoreRing score={score.score} tier={score.tier} size={72} strokeWidth={7} showLabel={false} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-sm sm:text-base text-slate-900">
                    Trust Score: {score.score}/100
                  </h3>
                  <p className="text-xs text-slate-600 capitalize">
                    {score.tier.replace(/_/g, ' ')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowTrust(true)}
                className="mt-3 text-xs text-amber-600 font-medium hover:underline inline-flex items-center gap-1"
                data-testid="trust-whats-contributing"
              >
                <Info className="h-3.5 w-3.5" /> What contributes to my score?
              </button>
            </CardContent>
          </Card>
        </section>

        {/* 7. Alerts */}
        <section data-testid="alerts-block">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-heading font-semibold text-base sm:text-lg text-slate-900">
              Your alerts
            </h2>
            {alertsCount > 0 && (
              <Link
                to="/alerts"
                className="text-xs font-medium text-amber-600 hover:underline"
                data-testid="alerts-see-all"
              >
                Manage
              </Link>
            )}
          </div>
          {alertsCount === 0 ? (
            <Card className="bg-white border-slate-200/70 shadow-soft">
              <CardContent className="p-4 sm:p-5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-slate-400" />
                  <p className="text-sm text-slate-700">
                    Get notified when new homes match your filters.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-300"
                  onClick={() => navigate('/search')}
                  data-testid="create-alert-btn"
                >
                  Create an alert
                </Button>
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-slate-600">
              {alertsCount} active alert{alertsCount === 1 ? '' : 's'}.
            </p>
          )}
        </section>

        {/* 8. Helpful tips */}
        <section data-testid="tips-block">
          <h2 className="font-heading font-semibold text-base sm:text-lg text-slate-900 mb-3">
            Helpful for renting in the GTA
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { title: 'How a verified listing works', body: 'What we check before any place goes live.' },
              { title: 'What "all-in pricing" really means', body: 'Rent, utilities, internet, fees — every cost.' },
              { title: 'Your first month checklist', body: 'Move-in day prep so nothing slips.' },
            ].map((c, i) => (
              <Link key={c.title} to="/help" className="block" data-testid={`tip-card-${i}`}>
                <Card className="h-full border-slate-200 bg-white hover:shadow-soft transition-shadow">
                  <CardContent className="p-4">
                    <h3 className="font-heading font-semibold text-sm text-slate-900 mb-1">{c.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{c.body}</p>
                    <p className="text-xs text-amber-600 font-medium mt-2 inline-flex items-center gap-1">
                      Read more <ArrowRight className="h-3 w-3" />
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      </main>

      {/* Footer (compact) */}
      <footer className="section-navy py-6" style={{ color: '#9A9388' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
          <p>© 2026 Sturent. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/help" className="hover:text-white transition-colors">Help</Link>
          </div>
        </div>
      </footer>

      {/* Trust Score breakdown dialog */}
      <Dialog open={showTrust} onOpenChange={setShowTrust}>
        <DialogContent
          className="w-[calc(100%-2rem)] sm:max-w-md bg-card-cream border-0 shadow-xl p-5 sm:p-6"
          data-testid="trust-breakdown-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-heading font-bold text-slate-900">
              How your Trust Score works
            </DialogTitle>
            <DialogDescription className="text-slate-600 mt-1.5 leading-relaxed">
              A Sturent-only signal that helps landlords see who you are at a glance.
              <strong className="block mt-2 text-slate-700 font-semibold">
                It is not a credit score and is not endorsed by any credit bureau.
              </strong>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 flex justify-center">
            <TrustScoreRing score={score.score} tier={score.tier} size={120} />
          </div>
          <ul className="mt-4 space-y-2 max-h-72 overflow-y-auto">
            {score.components.map((c) => (
              <li
                key={c.key}
                className={`flex items-center justify-between py-1.5 px-2 rounded ${
                  c.present ? 'bg-emerald-50' : ''
                }`}
              >
                <span className={`text-sm ${c.present ? 'text-emerald-800 font-medium' : 'text-slate-700'}`}>
                  {c.present ? '✓ ' : ''}
                  {c.label}
                </span>
                <span className={`text-xs ${c.present ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                  +{c.weight}
                </span>
              </li>
            ))}
          </ul>
          <Link
            to="/trust-score"
            onClick={() => setShowTrust(false)}
            className="mt-4 text-center block text-xs text-amber-600 font-medium hover:underline"
          >
            See full Trust Score page →
          </Link>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
